import { createDeepEqualSelector, RootState } from '@/store/store';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { BalanceMap, HyperLiquidBalances } from './balancesTypes';
import { ExchangeAccount } from '../exchanges/exchangesSliceTypes';
import { StorageEntry } from '../storage/storageTypes';

export interface WalletSelectorProps {
    accountId: number;
    exchangeId?: EXCHANGES;
}

export type AssetByExchange = {
    [exchangeId in EXCHANGES]: {
        value: number;
        valueUsd: number;
        allocation: number;
        exchangeId: EXCHANGES;
        asset: string;
    };
};

export interface BalanceByAsset {
    asset: string;
    value: number;
    valueUsd: number;
    allocation: number;
    price: number;
    byExchange: AssetByExchange | {};
}

export interface WalletBalance {
    usd: number;
    walletType: string;
    percent: number;
    label: string;
    digits?: number;
    value: number;
}

export type Asset = {
    asset: string;
    value: number;
    exchangeId: EXCHANGES | 'storage';
    accountId: number;
    valueUsd: number;
    allocation: number;
};

export interface BalanceForConversion {
    symbol: string;
    value: number;
    exchange: EXCHANGES;
    apiKey: number;
}

export const balancesSelector = (state: any) => state.balances.balances;
export const walletSelector = (state: RootState, props: WalletSelectorProps): WalletSelectorProps =>
    props;
export const totalValuesSelector = (state: RootState) => state.balances.totalValues;
export const accountIdSelector = (state, { accountId }): number => accountId;
export const balancesStateSelector = (state: RootState) => state.balances;
export const selectTotalValuesByAccountId = createDeepEqualSelector(
    [totalValuesSelector, walletSelector],
    (
        totalValues,
        props,
    ): {
        totalDeposits: number;
        totalWithdrawals: number;
        totalDepositsBtc: number;
        totalWithdrawalsBtc: number;
    } => {
        const { accountId } = props;

        const totalDepositsBtc = 0;
        const totalWithdrawalsBtc = 0;
        return {
            totalDepositsBtc,
            totalWithdrawalsBtc,
            ...totalValues[accountId],
        };
    },
);

export const selectHyperLiquidBalancesById = createDeepEqualSelector(
    [balancesStateSelector, (state, { accountId }: { accountId: number }) => accountId],
    (balancesState, apiKeyId): HyperLiquidBalances[] => {
        const values = balancesState.hyperLiquidBalances?.[apiKeyId];
        return Array.isArray(values) ? values : [];
    },
);

export const selectBalancesByAsset = (
    accounts: ExchangeAccount[],
    balances: BalanceMap,
    storage: StorageEntry[],
): Asset[] => {
    const storageList = storage || [];

    const assetsList: Asset[] = storageList.map((storageItem) => ({
        asset: storageItem.asset,
        value: storageItem.amount,
        exchangeId: 'storage',
        accountId: 0,
        valueUsd: 0,
        allocation: 0,
    }));

    accounts.forEach((account) => {
        const { id, exchangeId } = account;
        if (balances?.[id]) {
            for (let walletType in balances?.[id]) {
                const accountBalances = Object.values(balances?.[id][walletType].balances);
                if (accountBalances.length > 0) {
                    accountBalances.forEach((balance) => {
                        const balanceToStore: any = {
                            asset: balance.symbol,
                            value: balance.value,
                            exchangeId,
                            accountId: id,
                            valueUsd: 0,
                            allocation: 0,
                        };

                        assetsList.push(balanceToStore);
                    });
                }
            }
        }
    });

    return assetsList;
};

export const selectShowDust = createDeepEqualSelector([balancesStateSelector], (balances) => {
    return balances?.showDustHyperLiquidBalances;
});
